import React, { useEffect } from 'react';
import { Search, Select, Spinner2 } from 'components';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  dealerApi,
  IDealerOptionsWithTickets,
  IDealerTicket,
  useAssignTicketToDealerMutation,
  useGetDealerTicketsQuery,
  useGetSubDealersByBranchDealerUserQuery,
  useSetArchiveDealerTicketsMutation,
  useUpdateDealerTicketsMutation,
} from 'thunks/RTKQuery/dealer';
import DealerLayout from 'components/Layout/Dealer/DealerLayout';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'react-bootstrap';
import { dealerCustomerApi } from 'thunks/RTKQuery/dealerCustomer';
import { ICustomerItem } from 'pages/Shared/DealerCustomers/Models';
import { handleRTKResponseToaster } from 'utils/handle-api-response';
import { authSlice, confirmationSlice, toastSlice } from 'slices';
import { Confirmation } from 'components/Modal';
import { DealerTicketsComponent, ISortBy } from './DealerTicketsComponent';
import { DealerCustomersDetailModal } from './CustomerDetailModel';
import { DealersDetailModal } from './DelearDetailModel';
import { ArchivalComponent } from './ArchivalComponent';

const defaultSortBy: ISortBy = { dataField: 'ticketId', order: 'asc' };
interface IDealerOption {
  label: string;
  value: string;
}

export const DealerTicketsContainer = () => {
  const {
    data: mainData,
    showDealerCustomerDetailModel,
    showDealerDetailsModel,
  } = useAppSelector((state) => state.auth);
  const { show: showConfirmation } = useAppSelector(
    (state) => state.confirmation
  );
  const { dealer } = mainData;
  const [page, setPage] = React.useState(1);
  const [sortBy, setSortBy] = React.useState(defaultSortBy);
  const [searchBy, setSearchBy] = React.useState('');
  const [dealerOptions, setDealerOptions] = React.useState<IDealerOption[]>([]);

  const [tickets, setTickets] = React.useState<any[]>([]);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { data: authData } = useAppSelector((state) => state.auth);
  const userSettings: any = authData?.user?.userSettings;
  const [archiveStatus, setArchiveStatus] = React.useState<boolean | undefined>(
    false
  );
  // this is the root dealer option who is logged-in
  const defaultDealerOption = {
    label: t('dealerManagement.tickets.all'),
    value: 'All',
  };

  const [selectedDealer, setSelectedDealer] = React.useState<IDealerOption>(
    defaultDealerOption
  );

  const [dealerTicketRow, setDealerTicketRow] = React.useState<IDealerTicket>();
  // fetches list of dealers to display in the dropdown
  const {
    data: dealerList,
    refetch: refetchDealerList,
  } = useGetSubDealersByBranchDealerUserQuery();

  // fetches list of tickets associated with the selected dealer
  const {
    data,
    error,
    isLoading,
    isFetching,
    refetch,
  } = useGetDealerTicketsQuery(
    {
      dealerId: selectedDealer.value,
      sortByColumn: sortBy.dataField,
      sortOrder: sortBy.order,
      page,
      sizePerPage: +userSettings.pageSize,
      search: searchBy,
      isArchived: archiveStatus,
    },
    {
      skip: dealer?.dealerId === undefined,
      refetchOnMountOrArgChange: true,
    }
  );

  const updateTicketDealer = (dealerTicketId: number, dealerId: string) => {
    if (dealerTicketId && dealerId) {
      setTickets((prevTickets) =>
        prevTickets.map((ticket) => {
          if (ticket.dealerTicketId === dealerTicketId) {
            return {
              ...ticket,
              dealerId,
            };
          }
          return ticket;
        })
      );
    }
  };

  const updateTicketCustomer = (
    dealerTicketId: number,
    customerObj: ICustomerItem
  ) => {
    if (dealerTicketId && customerObj.dealerCustomerId) {
      setTickets((prevTickets) =>
        prevTickets.map((ticket) => {
          if (ticket.dealerTicketId === dealerTicketId) {
            return {
              ...ticket,
              customer: {
                ...ticket.customer,
                id: customerObj.dealerCustomerId,
                company: customerObj.customerInfo.company,
                lastName: customerObj.customerInfo.lastName,
              },
            };
          }
          return ticket;
        })
      );
    }
  };

  useEffect(() => {
    if (!Array.isArray(dealerList) || !dealerList.length) return;

    const options: IDealerOption[] = [defaultDealerOption];
    dealerList.forEach((subDealer: IDealerOptionsWithTickets) => {
      if (subDealer.hasTickets) {
        options.push({
          label: `${subDealer.dealerId} (${subDealer.dealerName})`,
          value: subDealer.dealerId,
        });
      }
    });
    setDealerOptions(options);
  }, [dealerList]);

  const fetchData = () => {
    refetch();
  };

  // update dealer-customer relation
  const [
    assignTicketToDealer,
    {
      isLoading: assignTicketLoading,
      error: assignTicketError,
      status: assignTicketStatus,
    },
  ] = useAssignTicketToDealerMutation();

  useEffect(() => {
    handleRTKResponseToaster(
      assignTicketStatus,
      assignTicketError,
      dispatch,
      t('contract.error'),
      t('dealer.save.title'),
      t('dealer.save.message')
    );
  }, [assignTicketStatus]);

  const assignTicketToNewDealer = async (
    dealerTicketId: number,
    dealerId: string,
    ticket: string,
    oldDealerId?: string
  ) => {
    if (dealerId && ticket) {
      try {
        await assignTicketToDealer({
          dealerId,
          tickets: [ticket],
          licenseAdminUser: `${dealer?.dealerId} (${dealer?.name})`,
          oldDealerId,
        });

        updateTicketDealer(dealerTicketId, dealerId);
        // Invalidate dealer tickets to refresh the list
        dealerApi.util.invalidateTags(['DealerTickets']);
        refetchDealerList();
      } catch (assignmentError) {
        toastSlice.actions.show({
          message: t('customerManagement.error.dealerTicket', {
            error:
              (assignmentError as any)?.message ||
              (assignmentError as any)?.data?.message,
          }),
          title: t('customerManagement.error.title'),
        });
      }
    }
  };

  // update dealer-customer relation
  const [
    updateDealerTicketsCustomer,
    {
      isLoading: customerLoading,
      error: customerSaveError,
      status: customerSaveStatus,
    },
  ] = useUpdateDealerTicketsMutation();

  useEffect(() => {
    handleRTKResponseToaster(
      customerSaveStatus,
      customerSaveError,
      dispatch,
      t('contract.error'),
      t('dealer.save.title'),
      t('dealer.save.message')
    );

    if (customerSaveStatus === 'fulfilled') {
      dealerCustomerApi.util.invalidateTags(['Customers']);
    }
  }, [customerSaveStatus]);

  // assignment of ticket to customer
  const assignTicketToCustomer = async (
    dealerTicketId: number,
    customerId: number,
    savedCustomerObj?: ICustomerItem
  ) => {
    if (dealerTicketId && customerId) {
      try {
        await updateDealerTicketsCustomer({
          dealerTicketId,
          customerId,
        });

        // Update local state
        if (savedCustomerObj) {
          updateTicketCustomer(dealerTicketId, savedCustomerObj);
        } else {
          setTickets((prevTickets) =>
            prevTickets.map((ticket) => {
              if (ticket.dealerTicketId === dealerTicketId) {
                return {
                  ...ticket,
                  customer: {
                    ...ticket.customer,
                    id: customerId,
                  },
                };
              }
              return ticket;
            })
          );
        }
      } catch (assignmentError) {
        toastSlice.actions.show({
          message: t('customerManagement.error.customerTicket', {
            error:
              (assignmentError as any)?.message ||
              (assignmentError as any)?.data?.message,
          }),
          title: t('customerManagement.error.title'),
        });
      }
    }
  };

  const openCustomerDialog = (row: IDealerTicket) => {
    setDealerTicketRow(row);
    dispatch(authSlice.actions.showDealerCustomerDetailModel());
  };

  const closeDialog = () => {
    dealerCustomerApi.util.invalidateTags(['RegistrationDbCustomer']);
    dispatch(authSlice.actions.closeDealerCustomerDetailModel());
  };

  useEffect(() => {
    fetchData();
  }, [dealer?.dealerId, page]);

  useEffect(() => {
    // if page is already 1 and any of the dependency is changed, then re-fetch data
    if (page === 1) {
      fetchData();
      return;
    }

    // else, set page to 1 and fetch data hook of change in page will be called
    setPage(1);
  }, [sortBy, selectedDealer, searchBy]);

  useEffect(() => {
    if (data?.tickets) {
      setTickets(data.tickets);
    }
  }, [data?.tickets]);
  const totalSize = data?.count ?? 0;

  const openDealerDialog = (row: IDealerTicket) => {
    setDealerTicketRow(row);
    dispatch(authSlice.actions.showDealerDetailsModel());
  };

  const closeDealerDialog = () => {
    dispatch(authSlice.actions.closeDealerDetailsModel());
  };

  // Actions section

  const [
    updateArchiveStatus,
    {
      isLoading: archiveTicketLoading,
      error: archiveTicketError,
      status: archiveTicketStatus,
    },
  ] = useSetArchiveDealerTicketsMutation();

  useEffect(() => {
    handleRTKResponseToaster(
      archiveTicketStatus,
      archiveTicketError,
      dispatch,
      t('contract.error'),
      t('dealer.save.title'),
      t('dealer.save.message')
    );
  }, [archiveTicketStatus]);

  const handleDelete = (ticket: IDealerTicket) => {
    if (ticket.ticketId) {
      const confirmationFlag = ticket.isArchived;

      dispatch(
        confirmationSlice.actions.show({
          title: confirmationFlag
            ? t('customerManagement.actions.unarchiveConfirm.title')
            : t('customerManagement.actions.archiveConfirm.title'),
          message: confirmationFlag
            ? t('customerManagement.actions.unarchiveConfirm.message')
            : t('customerManagement.actions.archiveConfirm.message'),
          buttonOk: confirmationFlag
            ? t('customerManagement.actions.unarchiveConfirm.buttonOk')
            : t('customerManagement.actions.archiveConfirm.buttonOk'),
          buttonCancel: t(
            'customerManagement.actions.archiveConfirm.buttonCancel'
          ),
          callback: async () => {
            dispatch(confirmationSlice.actions.close());
            updateArchiveStatus({
              ticketId: ticket.ticketId,
              isArchived: !ticket.isArchived,
            });
          },
        })
      );
    }
  };

  //

  return (
    <DealerLayout>
      <Row style={{ alignItems: 'baseline' }}>
        <Col lg={3}>
          <h1 className="ml-2 mb-4 mt-3">
            {t('dealerManagement.tickets.title')}
          </h1>
        </Col>
        <Col lg={3}>
          {!isLoading && (
            <h6 style={{ color: 'cornflowerblue' }}>
              {t('dealerManagement.tickets.ticketsFound', { totalSize })}
            </h6>
          )}
        </Col>
        <Col lg={1}>
          <h6 className="h6-short">
            {t('dealerManagement.tickets.dealersList')}
          </h6>
        </Col>
        <Col lg={5}>
          <Select
            options={dealerOptions}
            defaultValue={defaultDealerOption}
            onChange={setSelectedDealer}
          />
        </Col>
      </Row>

      <hr />
      <Row className="mt-3">
        <Spinner2
          show={
            isLoading ||
            isFetching ||
            customerLoading ||
            assignTicketLoading ||
            archiveTicketLoading
          }
        />
        {error && (
          <div className="alert alert-danger">
            {[error].filter(Boolean).join(' | ')}
          </div>
        )}
        <Col lg={8} className="mb-3">
          <Search
            initialValue={searchBy}
            onChange={(v) => setSearchBy(v)}
            className="bordered-search"
            clearButton
          />{' '}
        </Col>
        <Col lg={4}>
          <ArchivalComponent
            archiveStatus={archiveStatus}
            setArchiveStatus={setArchiveStatus}
          />
        </Col>
        {tickets && tickets.length > 0 && (
          <>
            <DealerTicketsComponent
              tickets={tickets}
              count={totalSize}
              setPage={setPage}
              setSortBy={setSortBy}
              sortBy={sortBy}
              page={page}
              sizePerPage={+userSettings.pageSize}
              isRootDealer={!!data?.isRootDealer}
              openCustomerDialog={openCustomerDialog}
              openDealerDialog={openDealerDialog}
              handleDelete={handleDelete}
            />
          </>
        )}
      </Row>
      {dealerTicketRow && (
        <DealerCustomersDetailModal
          show={showDealerCustomerDetailModel}
          dealerTicketRow={dealerTicketRow}
          rootDealer={{ dealerId: dealerTicketRow.dealerId } as any}
          loggedInDealer={dealer}
          assignTicketToCustomer={assignTicketToCustomer}
          onClose={() => {
            closeDialog();
          }}
        />
      )}

      {dealerTicketRow && (
        <DealersDetailModal
          show={showDealerDetailsModel}
          title={t('dealerManagement.dealerInformation')}
          assignTicketToNewDealer={assignTicketToNewDealer}
          dealerList={dealerList}
          rowItem={dealerTicketRow}
          onClose={() => {
            closeDealerDialog();
          }}
        />
      )}
      {showConfirmation && <Confirmation />}
    </DealerLayout>
  );
};
